import type { StepCrumb, StepMenuBeat } from "./StepperBar/StepperBar";
import type { BeatStatus, Narrative, NarrativeBeat, NarrativeStep } from "./types";

import { beatHasVerbatim } from "./types";

/** A beat with its step context, from flattening the narrative in render order. */
export interface FlatBeat {
    beat: NarrativeBeat;
    step: NarrativeStep;
    stepIndex: number;
}

export interface NarrativeView {
    crumbs: StepCrumb[];
    menuBeats: StepMenuBeat[];
    /** All beats in render order — the panel renders one stable keyed list from this. */
    ordered: FlatBeat[];
    /** Index of the active beat in `ordered`. */
    currentIndex: number;
    /** e.g. "Step 3 of 4: Offer". */
    stepLabel: string;
}

/** Menu/label text for a beat — "Offer 3/5" for offer beats, the step label otherwise. */
const beatLabel = (fb: FlatBeat): string =>
    fb.step.kind === "offer" ? `Offer ${fb.beat.index}/${fb.beat.of}` : fb.step.label;

/**
 * Derive everything the panel renders from the narrative, the per-beat status map, and which beat
 * is active. Pure — no React, unit-testable.
 */
export function buildNarrativeView(
    narrative: Narrative,
    statuses: Record<string, BeatStatus>,
    currentBeatId: string,
): NarrativeView {
    const flat: FlatBeat[] = narrative.steps.flatMap((step, stepIndex) =>
        step.beats.map((beat) => ({ beat, step, stepIndex })),
    );

    // A structurally-empty narrative (no beats in any step) has no active beat. This is type-valid
    // (steps/beats are plain arrays) and reachable from a malformed producer payload, so return a
    // zero-state view instead of dereferencing an undefined active beat below.
    if (flat.length === 0) {
        return { crumbs: [], menuBeats: [], ordered: [], currentIndex: 0, stepLabel: "" };
    }

    const foundIndex = flat.findIndex((fb) => fb.beat.id === currentBeatId);
    const currentIndex = foundIndex < 0 ? 0 : foundIndex;
    const active = flat[currentIndex];

    const crumbs: StepCrumb[] = narrative.steps.map((step, stepIndex) => {
        const isCurrent = stepIndex === active.stepIndex;
        const state: StepCrumb["state"] = isCurrent
            ? "current"
            : stepIndex < active.stepIndex
              ? "completed"
              : "upcoming";
        const label = isCurrent && step.kind === "offer" ? `Offer ${active.beat.index}/${active.beat.of}` : step.label;
        return { label, state };
    });

    const menuBeats: StepMenuBeat[] = flat.map((fb) => ({
        id: fb.beat.id,
        label: beatLabel(fb),
        required: beatHasVerbatim(fb.beat),
        status: statuses[fb.beat.id] ?? "pending",
        current: fb.beat.id === active.beat.id,
    }));

    return {
        crumbs,
        menuBeats,
        ordered: flat,
        currentIndex,
        stepLabel: `Step ${active.stepIndex + 1} of ${narrative.steps.length}: ${active.step.label}`,
    };
}
